import { createContext, useCallback, useContext, useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react'
import type { Provider, User } from '@supabase/supabase-js'
import { syncAccountData } from '../lib/cloudSync'
import { clearLocalData } from '../lib/storage'
import { supabase, supabaseConfigured } from '../lib/supabase'

type AccountContextValue = {
  user: User | null
  loading: boolean
  syncing: boolean
  status: string
  downloaded: boolean
  recovery: boolean
  syncNow: () => Promise<void>
  signOut: (clearDevice?: boolean) => Promise<void>
  saveProfile: (displayName: string) => Promise<void>
  requestPasswordReset: (email: string) => Promise<void>
  updatePassword: (password: string) => Promise<void>
  signInWithOAuth: (provider: Extract<Provider, 'google' | 'apple'>) => Promise<void>
  deleteAccount: (clearDevice?: boolean) => Promise<void>
}

const AccountContext = createContext<AccountContextValue | null>(null)

export function AccountProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(supabaseConfigured)
  const [syncing, setSyncing] = useState(false)
  const [status, setStatus] = useState('')
  const [downloaded, setDownloaded] = useState(false)
  const [recovery, setRecovery] = useState(false)
  const activeSync = useRef<Promise<void> | null>(null)

  useEffect(() => {
    if (!supabase) return
    let alive = true
    supabase.auth.getSession().then(({ data, error }) => {
      if (!alive) return
      if (error) setStatus(error.message)
      setUser(data.session?.user || null)
      setLoading(false)
    })
    const { data } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user || null)
      setLoading(false)
      if (event === 'PASSWORD_RECOVERY') setRecovery(true)
      if (!session) { setDownloaded(false); setStatus(''); setRecovery(false) }
    })
    return () => { alive = false; data.subscription.unsubscribe() }
  }, [])

  const syncNow = useCallback(async () => {
    if (!supabase || !user) return
    if (activeSync.current) return activeSync.current
    const operation = (async () => {
      setSyncing(true); setStatus('Syncing…')
      try {
        const result = await syncAccountData(supabase, user.id)
        if (result.downloaded) setDownloaded(true)
        setStatus(result.downloaded ? 'Cloud changes downloaded' : `Synced ${new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`)
      } catch (error) { setStatus(error instanceof Error ? error.message : 'Cloud sync failed.') }
      finally { setSyncing(false); activeSync.current = null }
    })()
    activeSync.current = operation
    return operation
  }, [user])

  useEffect(() => {
    if (!user) return
    void syncNow()
    const timer = window.setInterval(() => void syncNow(), 30000)
    const onFocus = () => void syncNow()
    window.addEventListener('focus', onFocus)
    window.addEventListener('online', onFocus)
    return () => { clearInterval(timer); window.removeEventListener('focus', onFocus); window.removeEventListener('online', onFocus) }
  }, [user, syncNow])

  const signOut = async (clearDevice = false) => {
    if (!supabase) return
    await syncNow()
    const { error } = await supabase.auth.signOut()
    if (error) throw error
    if (clearDevice) { await clearLocalData(); window.location.reload() }
  }

  const saveProfile = async (displayName: string) => {
    if (!supabase || !user) return
    const { data, error } = await supabase.auth.updateUser({ data: { ...user.user_metadata, display_name: displayName.trim() } })
    if (error) throw error
    setUser(data.user)
  }

  const requestPasswordReset = async (email: string) => {
    if (!supabase) return
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), { redirectTo: `${window.location.origin}/?account=recovery` })
    if (error) throw error
  }

  const updatePassword = async (password: string) => {
    if (!supabase) return
    const { error } = await supabase.auth.updateUser({ password })
    if (error) throw error
    setRecovery(false)
  }

  const signInWithOAuth = async (provider: Extract<Provider, 'google' | 'apple'>) => {
    if (!supabase) return
    const { error } = await supabase.auth.signInWithOAuth({ provider, options: { redirectTo: window.location.origin } })
    if (error) throw error
  }

  const deleteAccount = async (clearDevice = false) => {
    if (!supabase || !user) return
    await syncNow()
    const { error } = await supabase.functions.invoke('delete-account', { method: 'POST' })
    if (error) throw error
    await supabase.auth.signOut({ scope: 'local' })
    if (clearDevice) await clearLocalData()
    window.location.reload()
  }

  return <AccountContext.Provider value={{ user, loading, syncing, status, downloaded, recovery, syncNow, signOut, saveProfile, requestPasswordReset, updatePassword, signInWithOAuth, deleteAccount }}>{children}</AccountContext.Provider>
}

type AccountMode = 'sign-in' | 'create' | 'forgot' | 'recovery'

export function AccountControl({ compact = false }: { compact?: boolean }) {
  const account = useContext(AccountContext)
  const [open, setOpen] = useState(false)
  const [mode, setMode] = useState<AccountMode>('sign-in')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [clearDevice, setClearDevice] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [deleteConfirmation, setDeleteConfirmation] = useState('')
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState('')
  if (!account) throw new Error('AccountControl must be inside AccountProvider.')

  useEffect(() => {
    if (account.recovery) { setMode('recovery'); setOpen(true) }
  }, [account.recovery])

  useEffect(() => {
    setDisplayName(String(account.user?.user_metadata?.display_name || ''))
  }, [account.user?.id, account.user?.user_metadata?.display_name])

  const run = async (action: () => Promise<void>, success?: string) => {
    setBusy(true); setMessage('')
    try { await action(); if (success) setMessage(success) }
    catch (error) { setMessage(error instanceof Error ? error.message : 'Account request failed.') }
    finally { setBusy(false) }
  }

  const authenticate = async (event: FormEvent) => {
    event.preventDefault()
    if (!supabase) return
    const client = supabase
    await run(async () => {
      if (mode === 'create') {
        const { data, error } = await client.auth.signUp({
          email: email.trim(), password,
          options: { emailRedirectTo: window.location.origin, data: { display_name: displayName.trim() } }
        })
        if (error) throw error
        setMessage(data.session ? 'Account created and signed in.' : 'Check your email to confirm your account, then sign in.')
      } else {
        const { error } = await client.auth.signInWithPassword({ email: email.trim(), password })
        if (error) throw error
        setMessage('Signed in. Your local data is syncing.')
      }
      setPassword('')
    })
  }

  const recoveryForm = <form onSubmit={event => { event.preventDefault(); void run(async () => { await account.updatePassword(password); setPassword(''); setMode('sign-in') }, 'Password updated.') }}>
    <p>Choose a new password for your account.</p>
    <label>New password<input type="password" autoComplete="new-password" minLength={6} required value={password} onChange={event => setPassword(event.target.value)} /></label>
    <button className="primary-button" disabled={busy}>{busy ? 'Please wait…' : 'Update password'}</button>
  </form>

  return <>
    <button className={`account-button ${compact ? 'compact' : ''}`} onClick={() => setOpen(true)} aria-label={account.user ? `Account: ${account.user.email}` : 'Account'}>
      <span className="account-avatar">{account.user?.email?.[0].toUpperCase() || 'A'}</span><span>{account.loading ? 'Loading…' : account.user ? displayName || 'Account' : 'Sign in'}</span>
    </button>
    {open && <div className="modal-backdrop" role="presentation" onPointerDown={event => { if (event.target === event.currentTarget && !account.recovery) setOpen(false) }}>
      <section className="modal account-modal" role="dialog" aria-modal="true" aria-labelledby="account-title">
        {!account.recovery && <button className="account-close" aria-label="Close account" onClick={() => setOpen(false)}>×</button>}
        <span className="eyebrow">Cloud account</span>
        <h2 id="account-title">{mode === 'recovery' || account.recovery ? 'Reset password' : account.user ? 'Your account' : mode === 'create' ? 'Create account' : mode === 'forgot' ? 'Forgot password' : 'Sign in'}</h2>
        {!supabaseConfigured ? <><p>Account services have not been configured for this installation. Your data remains stored locally.</p><code>Set VITE_SUPABASE_URL and VITE_SUPABASE_PUBLISHABLE_KEY.</code></> : mode === 'recovery' || account.recovery ? recoveryForm : account.user ? <>
          <p>Signed in as <strong>{account.user.email}</strong>. Local data stays available offline and syncs when a connection is available.</p>
          <form onSubmit={event => { event.preventDefault(); void run(() => account.saveProfile(displayName), 'Profile saved.') }}>
            <label>Display name<input autoComplete="name" maxLength={80} value={displayName} onChange={event => setDisplayName(event.target.value)} /></label>
            <button disabled={busy}>Save profile</button>
          </form>
          <div className="account-sync-state" role="status">{account.syncing ? 'Syncing…' : account.status || 'Ready to sync'}</div>
          {account.downloaded && <button className="primary-button" onClick={() => window.location.reload()}>Reload cloud changes</button>}
          <button disabled={account.syncing} onClick={() => void account.syncNow()}>Sync now</button>
          <button onClick={() => { setMode('recovery'); setMessage('') }}>Change password</button>
          <label className="account-checkbox"><input type="checkbox" checked={clearDevice} onChange={event => setClearDevice(event.target.checked)} />Erase local boards and game data from this device after signing out</label>
          <button className="account-signout" onClick={() => void run(async () => { await account.signOut(clearDevice); setOpen(false) })} disabled={busy}>Sign out</button>
          <button className="account-delete-toggle" onClick={() => setDeleting(value => !value)}>{deleting ? 'Cancel account deletion' : 'Delete account…'}</button>
          {deleting && <section className="account-danger-zone">
            <strong>Permanently delete cloud account</strong>
            <p>This removes your sign-in and cloud copy. Type DELETE to confirm.</p>
            <label>Confirmation<input value={deleteConfirmation} onChange={event => setDeleteConfirmation(event.target.value)} /></label>
            <label className="account-checkbox"><input type="checkbox" checked={clearDevice} onChange={event => setClearDevice(event.target.checked)} />Also erase local data from this device</label>
            <button className="account-delete" disabled={busy || deleteConfirmation !== 'DELETE'} onClick={() => void run(() => account.deleteAccount(clearDevice))}>Permanently delete account</button>
          </section>}
        </> : mode === 'forgot' ? <form onSubmit={event => { event.preventDefault(); void run(() => account.requestPasswordReset(email), 'If an account exists for this email, a reset link has been sent.') }}>
          <p>Enter your email and we’ll send a password-reset link.</p>
          <label>Email<input type="email" autoComplete="email" required value={email} onChange={event => setEmail(event.target.value)} /></label>
          <button className="primary-button" disabled={busy}>{busy ? 'Please wait…' : 'Send reset link'}</button>
          <button type="button" onClick={() => { setMode('sign-in'); setMessage('') }}>Back to sign in</button>
        </form> : <>
          <form onSubmit={authenticate}>
            {mode === 'create' && <label>Display name<input autoComplete="name" maxLength={80} value={displayName} onChange={event => setDisplayName(event.target.value)} /></label>}
            <label>Email<input type="email" autoComplete="email" required value={email} onChange={event => setEmail(event.target.value)} /></label>
            <label>Password<input type="password" autoComplete={mode === 'create' ? 'new-password' : 'current-password'} minLength={6} required value={password} onChange={event => setPassword(event.target.value)} /></label>
            <button className="primary-button" disabled={busy}>{busy ? 'Please wait…' : mode === 'create' ? 'Create account' : 'Sign in'}</button>
          </form>
          <div className="account-oauth" aria-label="Social sign in">
            <span>or continue with</span>
            <div><button disabled={busy} onClick={() => void run(() => account.signInWithOAuth('google'))}>Google</button><button disabled={busy} onClick={() => void run(() => account.signInWithOAuth('apple'))}>Apple</button></div>
          </div>
          {mode === 'sign-in' && <button className="account-switch" onClick={() => { setMode('forgot'); setMessage('') }}>Forgot password?</button>}
          <button className="account-switch" onClick={() => { setMode(value => value === 'create' ? 'sign-in' : 'create'); setMessage('') }}>{mode === 'create' ? 'Already have an account? Sign in' : 'New coach? Create an account'}</button>
        </>}
        {message && <p className="account-message" role="status">{message}</p>}
      </section>
    </div>}
  </>
}
